// The composer's attached-context band: pills wrap into a pile instead of
// scrolling off-screen, but a large selection collapses behind a "+N"
// count so the band never dwarfs the composer.
import { X } from 'lucide-react';
import { createContext, type ReactNode, useContext, useState } from 'react';

const VISIBLE_PILL_COUNT = 3;

const AttachmentPillBandContext = createContext(false);

/** True inside an AttachmentPillBand. Pills whose labels wrap in prose
 * (shift and callout references show full time ranges) read this to stay
 * single-line instead, so a long label cannot grow the band's height. */
export function useInAttachmentPillBand(): boolean {
    return useContext(AttachmentPillBandContext);
}

export function AttachmentPillBand({
    pills,
    onClear,
    clearLabel,
    clearTestId,
}: Readonly<{
    readonly pills: ReadonlyArray<{
        readonly id: string;
        /** Must render single-line: the band bounds its height by pill
         * count. CSS cannot impose ellipsis truncation on an opaque
         * subtree from outside, so the contract lives with the pill.
         * EntityPill/ReferenceBadge comply automatically by reading
         * useInAttachmentPillBand; any other pill implementation owns its
         * truncation, as the staged reference chips do. */
        readonly pill: ReactNode;
    }>;
    readonly onClear: () => void;
    readonly clearLabel: string;
    readonly clearTestId?: string;
}>) {
    // Expansion is keyed to the pill ids, so replacing the selection with a
    // different pile collapses back to +N while label re-resolution (same
    // ids) keeps an expanded pile open.
    const pillsKey = pills.map(({ id }) => id).join('\n');
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const expanded = expandedKey === pillsKey;
    const hiddenCount = pills.length - VISIBLE_PILL_COUNT;
    const visiblePills =
        expanded || hiddenCount <= 0
            ? pills
            : pills.slice(0, VISIBLE_PILL_COUNT);
    return (
        <div className="flex items-start gap-2">
            <AttachmentPillBandContext.Provider value={true}>
                <ul className="text-v3-chat-meta m-0 flex min-w-0 flex-1 list-none flex-wrap items-center gap-x-1.5 gap-y-1 p-0">
                    {visiblePills.map(({ id, pill }) => (
                        <li key={id} className="inline-flex max-w-full min-w-0">
                            {pill}
                        </li>
                    ))}
                    {!expanded && hiddenCount > 0 && (
                        <li className="inline-flex">
                            <button
                                type="button"
                                aria-label={`Show ${hiddenCount} more attached ${
                                    hiddenCount === 1 ? 'record' : 'records'
                                }`}
                                data-testid="attachment-pill-expand"
                                className="text-muted-foreground bg-muted hover:bg-accent hover:text-foreground focus-visible:outline-agent-action my-[1.5px] inline-flex items-center rounded-md px-1.5 py-px font-medium outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
                                onClick={() => setExpandedKey(pillsKey)}
                            >
                                +{hiddenCount}
                            </button>
                        </li>
                    )}
                </ul>
            </AttachmentPillBandContext.Provider>
            <button
                type="button"
                className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-agent-action grid size-5 shrink-0 place-items-center rounded focus-visible:outline-2"
                aria-label={clearLabel}
                title="Clear selection"
                data-testid={clearTestId}
                onClick={onClear}
            >
                <X aria-hidden="true" className="size-3.5" />
            </button>
        </div>
    );
}
